import { Fragment } from 'react';
import { CURRENCY_USD } from 'constants/currency';
import { getProfilePageUrl } from 'constants/pages';
import type { ConnectionGame, ConnectionRequest } from 'types/connections';
import { EventTypesEnum } from 'types/generated/client';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { formatVenueDateParts } from 'utils/shared/time/formatVenueTimeRange';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import CheckCircle from 'svg/CheckCircle';
import CloseIcon from 'svg/CloseIcon';
import ShieldCheckTrusted from 'svg/ShieldCheckTrusted';
import ActivityBadge from 'components/ActivityBadge';
import BadgeRating from 'components/BadgeRating';
import Button, { ButtonLink } from 'components/Button';
import DetailModalShell from 'components/Find/DetailModalShell';

interface Props {
  isOpen: boolean;
  request: ConnectionRequest | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}

function GameCard({ game }: { game: ConnectionGame }) {
  const dateParts = formatVenueDateParts(game.startDateTime, game.endDateTime, game.timezone);

  return (
    <li className="flex flex-col gap-ds-xs rounded-2xl bg-color-bg-lightmode-secondary p-ds-md dark:bg-color-bg-darkmode-secondary">
      <div className="flex items-center justify-between gap-ds-sm">
        <ActivityBadge
          type={EventTypesEnum.OpenPlay}
          sport={game.sport}
          competitiveness={game.competitiveness}
          size="xs"
        />
        {game.priceUnitAmount !== null && (
          <p className="typography-product-card-title-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {convertUnitPriceToFormattedPrice(game.priceUnitAmount, CURRENCY_USD).priceDisplay}
          </p>
        )}
      </div>
      <p className="typography-product-card-title-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        {game.title}
      </p>
      <p className="typography-product-chips-filters flex items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        {dateParts.map((part, index) => (
          <Fragment key={part}>
            {index > 0 && <span className="size-1 shrink-0 rounded-full bg-brand-fire-600" />}
            <span>{part}</span>
          </Fragment>
        ))}
      </p>
      <p className="typography-product-text-card-mobile flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        <img src="/images/app/pin-gradient.svg" alt="" className="h-4 w-auto shrink-0" />
        <span className="truncate">{game.venueName}</span>
      </p>
    </li>
  );
}

export default function ConnectionRequestModal({
  isOpen,
  request,
  isSubmitting = false,
  onClose,
  onAccept,
  onDecline,
}: Props) {
  if (!request) {
    return null;
  }

  const { player, games, message } = request;

  const footer = (
    <div className="flex gap-ds-lg">
      <Button
        variant="primary"
        size="lg"
        disabled={isSubmitting}
        onClick={onDecline}
        iconLeft={<CloseIcon className="size-5" />}
        className="flex-1"
      >
        Decline
      </Button>
      <Button
        variant="brand"
        size="lg"
        loading={isSubmitting}
        onClick={onAccept}
        iconLeft={<CheckCircle className="size-5" />}
        className="flex-1"
      >
        Accept
      </Button>
    </div>
  );

  return (
    <DetailModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Request"
      footer={footer}
      hasFooterDivider
    >
      <div className="flex flex-col gap-ds-xl">
        <section className="flex flex-col gap-ds-md">
          <h3 className="typography-product-heading-compact-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            From player
          </h3>
          <div className="flex items-center gap-ds-md">
            <div className="relative size-10 shrink-0">
              <img
                src={getProfileImageUrlOrPlaceholder({ path: player.profileImagePath })}
                alt=""
                className="size-full rounded-full object-cover"
              />
              {player.isVerified && (
                <span className="absolute bottom-0 right-0 rounded-full bg-color-bg-lightmode-primary p-[0.1rem] dark:bg-color-bg-darkmode-primary">
                  <ShieldCheckTrusted className="block size-[0.8rem]" />
                </span>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-ds-xs">
              <p className="typography-product-card-title-mobile truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {player.fullName}
              </p>
              {!!player.skillLevel && (
                <BadgeRating
                  level={player.skillLevel}
                  sport={player.sport}
                  variant="neutral"
                  size="xs"
                  className="h-4 gap-0.5 self-start px-ds-xs"
                />
              )}
            </div>
            <ButtonLink
              href={getProfilePageUrl(player.username)}
              variant="secondary"
              size="sm"
              isInline
              className="shrink-0"
            >
              View profile
            </ButtonLink>
          </div>
        </section>

        {games.length > 0 && (
          <section className="flex flex-col gap-ds-md">
            <h3 className="typography-product-heading-compact-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Games you played together ({games.length})
            </h3>
            <ul className="flex flex-col gap-ds-md">
              {games.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </ul>
          </section>
        )}

        {!!message && (
          <section className="flex flex-col gap-ds-md">
            <h3 className="typography-product-heading-compact-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Player&apos;s message
            </h3>
            <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {message}
            </p>
          </section>
        )}
      </div>
    </DetailModalShell>
  );
}
